import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Calendar, Gauge, Fuel, Settings as Cog, Truck } from "lucide-react";
import { COUNTRY_FLAGS, BODY_LABELS, FUEL_LABELS, TRANSMISSION_LABELS, formatPrice, formatMileage, resolveImg } from "@/lib/api";

const STATUS_LABELS = {
  available: { text: "В наличии", cls: "status-available" },
  order: { text: "Под заказ", cls: "status-order" },
  sold: { text: "Продано", cls: "status-sold" },
};

export default function CarCard({ car }) {
  const photo = car.photos?.[0] ? resolveImg(car.photos[0]) : null;
  const flag = COUNTRY_FLAGS[car.country_origin] || "";
  const status = STATUS_LABELS[car.status] || STATUS_LABELS.available;

  return (
    <article className="car-card" data-testid={`car-card-${car.slug}`}>
      <Link to={`/cars/${car.slug}`} className="block relative aspect-[16/10] bg-gray-100 overflow-hidden">
        {photo ? (
          <img src={photo} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 font-headings text-2xl">{car.brand}</div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span className={`status-badge ${status.cls}`} data-testid={`car-status-${car.slug}`}>{status.text}</span>
          {car.customs_cleared && (
            <span className="status-badge" style={{ background: "rgba(45,122,58,0.95)", color: "#FFFFFF" }}>Растаможен</span>
          )}
        </div>
        {car.country_origin && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md backdrop-blur-sm flex items-center gap-1.5" style={{ background: "rgba(15,36,68,0.85)" }}>
            <span className="text-base leading-none">{flag}</span>
            <span className="font-accent text-[11px] uppercase tracking-wider text-white">{car.country_origin}</span>
          </div>
        )}
      </Link>

      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <Link to={`/cars/${car.slug}`} className="block font-headings font-bold text-base md:text-lg text-[var(--color-text-primary)] hover:text-[var(--color-secondary)] transition-colors line-clamp-1">
              {car.brand} {car.model}
            </Link>
            <div className="font-accent text-xs uppercase tracking-wider text-[var(--color-text-secondary)] mt-1">
              {car.year} год · {formatMileage(car.mileage)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
          {car.body_type && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-secondary)]"><Cog size={13} /> {BODY_LABELS[car.body_type] || car.body_type}</div>
          )}
          {car.fuel_type && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-secondary)]"><Fuel size={13} /> {FUEL_LABELS[car.fuel_type] || car.fuel_type}</div>
          )}
          {car.transmission && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-secondary)]"><Gauge size={13} /> {TRANSMISSION_LABELS[car.transmission] || car.transmission}</div>
          )}
          {car.euro_standard && (
            <div className="flex items-center gap-1.5" style={{ color: "var(--color-secondary)" }}><Calendar size={13} /> {car.euro_standard}</div>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-3 border-t border-[var(--color-border)]">
          <div>
            <div className="price-tag" data-testid={`car-price-${car.slug}`}>{formatPrice(car.price)}</div>
            {car.delivery_days > 0 && !car.customs_cleared && (
              <div className="flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] mt-1">
                <Truck size={11} /> доставка ~{car.delivery_days} дн.
              </div>
            )}
          </div>
          <Link
            to={`/cars/${car.slug}`}
            className="inline-flex items-center gap-1 font-accent text-xs uppercase tracking-wider font-semibold text-[var(--color-secondary)] hover:text-[var(--color-primary)] transition-colors"
            data-testid={`car-detail-link-${car.slug}`}
          >
            Подробнее <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}
